import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, Card, message, Skeleton } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import {
  FaClinicMedical,
  FaCalendarAlt,
  FaClock,
  FaUserMd,
} from "react-icons/fa";
import { GrNotes } from "react-icons/gr";
import { motion } from "framer-motion";
import TimeSlotSelect from "../../Booking/TimeSLotSelect";
import DateSelect from "../../Booking/DateSelect";
import CliniSelect from "../../Booking/CliniSelect";
import DoctorSelect from "../../Booking/DoctorSelect";
import drEasyApi from "../../../Api/Customapi";
import { useDoctorQuery } from "../../../hooks/useDoctorQuery";
import getErrorMessage from "../../Errors Handling/getErrorMssg";
import FormAnswer from "../../Booking/FormAnswer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import i18n from "../../../i18n";
import { toLocaleDigits } from "../../../utils/helpers";
import OptionSelect from "../../Booking/OptionSelect";
import { slug } from "../../../utils/store";

export default function Booking() {
  const { t, currentLanguage } = useLanguage();
  const {
    data: doctor,
    isLoading: isDoctorLoading,
    error: doctorError,
  } = useDoctorQuery();
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [optinSelected, setOptionSelected] = useState(false);
  
  const [generalOptionSelected, setGeneralOptionSelected] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [notes, setNotes] = useState("");
  const [isBooking, setIsBooking] = useState(false);
  const [hasFilledForm, setHasFilledForm] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isCheckingForm, setIsCheckingForm] = useState(true);
  const [formError, setFormError] = useState(null);
  const [isGuestBooking, setIsGuestBooking] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const bookingRef = useRef(null);
  const firstSectionRef = useRef(null);
  const clinicSectionRef = useRef(null);
  const optionSectionRef = useRef(null);
  const doctorSectionRef = useRef(null);
  const dateSectionRef = useRef(null);
  const timeSectionRef = useRef(null);
  const hourSectionRef = useRef(null);
  const confirmationSectionRef = useRef(null);

  const scrollToSection = useCallback((sectionRef, delay = 300) => {
    setTimeout(() => {
      if (sectionRef.current) {
        const headerHeight = 80;
        const elementTop =
          sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementTop - headerHeight, behavior: "smooth" });
      }
    }, delay);
  }, []);

  const generateHourlySlots = (startTime, endTime) => {
    const slots = [];
    const start = dayjs(`2000-01-01 ${startTime}`);
    const end = dayjs(`2000-01-01 ${endTime}`);

    let current = start;
    while (current.isBefore(end)) {
      const next = current.add(1, "hour");
      if (next.isAfter(end)) break;

      slots.push({
        startTime: current.format("HH:mm"),
        endTime: next.format("HH:mm"),
      });

      current = next;
    }
    return slots;
  };
  const formatTimeOnly = (timeStr, lang) => {
    if (!timeStr) return "";

    const [hour, minute] = timeStr.split(":");
    let h = parseInt(hour, 10);

    const isPM = h >= 12;
    const formattedHour = h % 12 || 12;

    const am = lang === "ar" ? "صباحًا" : "AM";
    const pm = lang === "ar" ? "مساءً" : "PM";

    const suffix = isPM ? pm : am;
    const result = `${formattedHour}:${minute} ${suffix}`;

    return lang === "ar" ? toLocaleDigits(result, "ar") : result;
  };

  const checkFormStatus = useCallback(async () => {
    if (isGuestBooking) {
      setHasFilledForm(true);
      setShowForm(false);
      setIsCheckingForm(false);
      return;
    }

    try {
      if (doctor?.id) {
        const response = await drEasyApi.get(
          `/api/Book/forms/CheckForm/${doctor.id}`,
        );

        if (response?.data?.formCompleted) {
          setHasFilledForm(true);
          setShowForm(false);
        } else {
          try {
            const formDataResponse = await drEasyApi.get(
              `/api/Book/forms/${doctor.id}`,
            );
            const hasQuestions =
              formDataResponse.data &&
              formDataResponse.data.questions &&
              formDataResponse.data.questions.length > 0;

            if (hasQuestions) {
              setHasFilledForm(false);
              setShowForm(true);
            } else {
              setHasFilledForm(true);
              setShowForm(false);
            }
          } catch (formFetchError) {
            setHasFilledForm(true);
            setShowForm(false);
          }
        }
      }
    } catch (error) {
      console.error("Error checking form status:", error);
      setFormError(getErrorMessage(error, t("booking.errors.checkingForm")));
      message.error(getErrorMessage(error, t("booking.errors.checkingForm")));
    } finally {
      setIsCheckingForm(false);
    }
  }, [doctor?.id, isGuestBooking, t]);

  useEffect(() => {
    const initializeBookingMode = async () => {
      const guestBookingFlag = sessionStorage.getItem(`isGuestBooking-${slug}`);
      const storedGuestInfo = sessionStorage.getItem(`guestInfo-${slug}`);

      if (guestBookingFlag === "true" && storedGuestInfo) {
        setIsGuestBooking(true);
        setGuestInfo(JSON.parse(storedGuestInfo));
        setHasFilledForm(true);
        setShowForm(false);
        setIsCheckingForm(false);
      } else {
        setIsGuestBooking(false);

        try {
          const res = await drEasyApi.get("/api/Book/CheckUser");
          setUserStatus(res.data);
        } catch (err) {
          console.error("Failed to fetch user status", err);
        }
      }

      setIsInitialized(true);
    };

    initializeBookingMode();
  }, [slug]);

  useEffect(() => {
    if (!isInitialized || isGuestBooking) {
      return;
    }

    if (doctor?.id) {
      checkFormStatus();
    } else if (!isDoctorLoading) {
      setIsCheckingForm(false);
    }
  }, [
    isInitialized,
    isGuestBooking,
    doctor?.id,
    isDoctorLoading,
    checkFormStatus,
  ]);

  useEffect(() => {
    if (showForm && bookingRef.current) {
      const timeout = setTimeout(() => {
        const headerHeight = 200;
        const top =
          bookingRef.current.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: top - headerHeight,
          behavior: "smooth",
        });
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [showForm]);

  useEffect(() => {
    if (doctor?.clinics && !selectedClinic) {
      const queryParams = new URLSearchParams(location.search);
      const clinicId = queryParams.get("ClinicId");
      const doctorId = queryParams.get("DoctorId");

      if (clinicId) {
        const defaultClinic = doctor.clinics.find(
          (clinic) => clinic.clinicId === clinicId,
        );
        if (defaultClinic) {
          setSelectedClinic(defaultClinic);

          if (doctorId) {
            const targetDoctor = defaultClinic?.doctors.find(
              (d) => d.doctorId === doctorId,
            );
            if (targetDoctor) {
              setTimeout(() => {
                setSelectedDoctor(targetDoctor);
              }, 100);
            }
          }
        }
      } else if (doctorId) {
        const clinicWithDoctor = doctor.clinics.find(
          (clinic) =>
            clinic?.doctors &&
            clinic?.doctors.some((d) => d.doctorId === doctorId),
        );

        if (clinicWithDoctor) {
          setSelectedClinic(clinicWithDoctor);
          const targetDoctor = clinicWithDoctor?.doctors.find(
            (d) => d.doctorId === doctorId,
          );
          if (targetDoctor) {
            setTimeout(() => {
              setSelectedDoctor(targetDoctor);
            }, 100);
          }
        }
      }
    }
  }, [doctor?.clinics, location.search, selectedClinic]);

  useEffect(() => {
    if (
      !isDoctorLoading &&
      !isCheckingForm &&
      !showForm &&
      firstSectionRef.current
    ) {
      const headerHeight = 200;
      const elementTop =
        firstSectionRef.current.getBoundingClientRect().top +
        window.pageYOffset;

      window.scrollTo({
        top: elementTop - headerHeight,
        behavior: "smooth",
      });
    }
  }, [isDoctorLoading, isCheckingForm, showForm]);

  useEffect(() => {
    if (selectedClinic && doctorSectionRef.current) {
      scrollToSection(doctorSectionRef, 900);
    }
  }, [selectedClinic, scrollToSection, optinSelected]);
 
  useEffect(() => {
    if (selectedClinic && optionSectionRef.current) {
      scrollToSection(optionSectionRef, 900);
    }
  }, [selectedClinic, scrollToSection, optinSelected]);
 
  useEffect(() => {
    if (selectedDoctor && dateSectionRef.current) {
      scrollToSection(dateSectionRef, 500);
    }
  }, [selectedDoctor, scrollToSection]);

  useEffect(() => {
    if (generalOptionSelected && dateSectionRef.current) {
      scrollToSection(dateSectionRef, 700);
    }
  }, [generalOptionSelected, scrollToSection, selectedClinic]);
  useEffect(() => {
    if (selectedDate && timeSectionRef.current) {
      scrollToSection(timeSectionRef, 500);
    }
  }, [selectedDate, scrollToSection]);

  useEffect(() => {
    if (
      selectedTimeSlot &&
      selectedTimeSlot.isHourly &&
      hourSectionRef.current
    ) {
      scrollToSection(hourSectionRef, 500);
    } else if (
      selectedTimeSlot &&
      !selectedTimeSlot.isHourly &&
      confirmationSectionRef.current
    ) {
      scrollToSection(confirmationSectionRef, 500);
    }
  }, [selectedTimeSlot, scrollToSection]);

  useEffect(() => {
    if (selectedHour && confirmationSectionRef.current) {
      scrollToSection(confirmationSectionRef, 500);
    }
  }, [selectedHour, scrollToSection]);

  const handleClinicSelect = (clinic) => {
    setSelectedClinic(null);
    setTimeout(() => {
      setSelectedClinic(clinic);
    }, 100);
    setSelectedDoctor(null);
    setOptionSelected(false);
    setGeneralOptionSelected(false);

    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setSelectedHour(null);
  };

  const handleDoctorSelect = (doctor) => {
    setSelectedDoctor(doctor);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setSelectedHour(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
    setSelectedHour(null);
  };

  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
    setSelectedHour(null);
  };

  const handleHourSelect = (hour) => {
    setSelectedHour(hour);
  };

  const handleFormSubmitSuccess = () => {
    setHasFilledForm(true);
    setShowForm(false);
    setTimeout(() => {
      scrollToSection(clinicSectionRef, 300);
    }, 500);
  };

  const handleBookAppointment = async () => {
    if (isBooking) {
      return;
    }

    if (!selectedTimeSlot) {
      message.warning(t("booking.messages.selectTimeSlot"));
      return;
    }

    if (selectedTimeSlot.isHourly && !selectedHour) {
      message.warning(t("booking.messages.selectHour"));
      return;
    }

    if (!isGuestBooking) {
      if (userStatus?.isLocked) {
        message.error(t("booking.messages.blocked"));
        return;
      }

      if (userStatus?.skippedCount > 0) {
        const warningMsg = t("booking.messages.skippedWarning").replace(
          "{count}",
          userStatus.skippedCount,
        );
        message.warning(warningMsg);
      }
    }

    setIsBooking(true);
    try {
      const baseBookingData = {
        availabilityHourId: selectedTimeSlot.availabilityHourId,
        bookingDate: dayjs(selectedDate).format("YYYY-MM-DD"),
        notes: notes,
        selectedHour:
          selectedTimeSlot.isHourly && selectedHour
            ? selectedHour.startTime
            : null,
        doctorId: selectedDoctor ? selectedDoctor.doctorId : null,
      };

      if (isGuestBooking && guestInfo) {
        const bookingData = {
          ...baseBookingData,
          fullName: guestInfo.fullName,
          email: guestInfo.email,
          phoneNumber: guestInfo.phoneNumber,
        };

        await drEasyApi.post("/api/Book/BookAsGuest", bookingData);
        message.success(t("booking.messages.successGuest"));

        sessionStorage.removeItem(`guestInfo-${slug}`);
        sessionStorage.removeItem(`isGuestBooking-${slug}`);

        navigate(`/home`);
      } else {
        await drEasyApi.post("/api/Book/Book", baseBookingData);
        message.success(t("booking.messages.success"));
        navigate(`/home`);
      }
    } catch (error) {
      console.error("Booking error:", error);
      const errorMsg = getErrorMessage(error, t("booking.messages.failed"));
      message.error(errorMsg);
    } finally {
      setIsBooking(false);
    }
  };

  const handleSkip = () => {
    setHasFilledForm(true);
    setShowForm(false);
    setTimeout(() => {
      scrollToSection(clinicSectionRef, 300);
    }, 500);
  };

  const formatTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return "";
    const [hour, minute] = timeStr.split(":");
    const hourNum = parseInt(hour, 10);
    if (isNaN(hourNum)) return timeStr;
    const ampm =
      hourNum >= 12 ? t("timeSlotSelect.pm") : t("timeSlotSelect.am");
    const formattedHour = hourNum % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };
  const getWorkingDaysForGeneral = () => {
    return selectedClinic?.genralDays || [];
  };

  const getWorkingDaysForSelectedDoctor = () => {
    if (!selectedDoctor || !selectedDoctor.workingDays) {
      return [];
    }
    return selectedDoctor.workingDays || [];
  };

  const sectionAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.8,
        ease: "easeOut",
      },
    }),
  };

  if (doctor?.isLocked) {
    return (
      <div className="  min-h-screen">
        <div className=" w-full md:max-w-5xl mx-auto p-6 text-center">
          <Card className="text-red-500 shadow-lg">
            <div className="p-8">
              <h2 className="text-lg md:text-2xl font-bold mb-4">
                {t("booking.doctorUnavailable.title")}
              </h2>
              <p className="text-sm md:text-lg mb-4">
                {t("booking.doctorUnavailable.description")}
              </p>
              <Button
                type="primary"
                size="large"
                onClick={() => navigate(-1)}
                className="bg-primary hover:bg-primary_hover"
              >
                {t("booking.doctorUnavailable.goBack")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (isDoctorLoading || isCheckingForm || !isInitialized) {
    return (
      <div className="  min-h-screen">
        <div className="max-w-5xl mx-auto p-6">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
    );
  }

  if (doctorError) {
    return (
      <div className="  min-h-screen">
        <div className="max-w-5xl mx-auto p-6 text-center">
          <Card className="text-red-500">
            <h2>{t("booking.errors.loadingDoctor")}</h2>
            <p>
              {getErrorMessage(doctorError, t("booking.errors.failedToLoad"))}
            </p>
            <Button type="primary" onClick={() => window.location.reload()}>
              {t("booking.errors.tryAgain")}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (formError && !showForm) {
    return (
      <div className="  min-h-screen">
        <div className="max-w-5xl mx-auto p-6 text-center">
          <Card className="text-red-500">
            <h2>{t("booking.errors.checkingForm")}</h2>
            <p>{formError}</p>
            <div className="mt-4 space-x-4">
              <Button type="primary" onClick={checkFormStatus}>
                {t("booking.errors.retry")}
              </Button>
              <Button onClick={() => setShowForm(true)}>
                {t("booking.errors.proceedToForm")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="  min-h-screen">
        <div ref={bookingRef} className="max-w-5xl mx-auto ">
          <FormAnswer
            doctorId={doctor?.id}
            onSubmit={handleFormSubmitSuccess}
            onSkip={handleSkip}
            onError={(error) =>
              setFormError(
                getErrorMessage(error, t("booking.errors.failedToSubmit")),
              )
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="  min-h-screen mb-5">
      <div
        ref={firstSectionRef}
        className="max-w-5xl mx-auto space-y-8 sm:px-0 px-3"
      >
        <motion.section
          ref={clinicSectionRef}
          initial="hidden"
          animate="visible"
          custom={1}
          variants={sectionAnimation}
          className="space-y-4"
        >
          <CliniSelect
            clinics={doctor?.clinics || []}
            onSelect={handleClinicSelect}
            selectedClinic={selectedClinic}
          />
        </motion.section>
        {selectedClinic && (
          <motion.section
            ref={optionSectionRef}
            initial="hidden"
            animate="visible"
            custom={1}
            variants={sectionAnimation}
            className="space-y-4"
          >
            <OptionSelect
              setOptionSelected={setOptionSelected}
              setGeneralOptionSelected={setGeneralOptionSelected}
              setSelectedDoctor={setSelectedDoctor}
              setSelectedDate={setSelectedDate}
              setSelectedTimeSlot={setSelectedTimeSlot}
              selectedClinic={selectedClinic}
            />
          </motion.section>
        )}

        {optinSelected && (
          <motion.section
            ref={doctorSectionRef}
            initial="hidden"
            animate="visible"
            custom={2}
            variants={sectionAnimation}
            className="space-y-4"
          >
            <DoctorSelect
              doctors={selectedClinic?.doctors || []}
              onSelect={handleDoctorSelect}
              selectedDoctor={selectedDoctor}
            />
          </motion.section>
        )}

        {generalOptionSelected && (
          <motion.section
            ref={dateSectionRef}
            initial="hidden"
            animate="visible"
            custom={3}
            variants={sectionAnimation}
            className="space-y-4"
          >
            <DateSelect
              workingDays={getWorkingDaysForGeneral()} // ✅ genralDays
              onSelect={handleDateSelect}
              selectedDate={selectedDate}
            />
          </motion.section>
        )}
        {selectedDoctor && (
          <motion.section
            ref={dateSectionRef}
            initial="hidden"
            animate="visible"
            custom={3}
            variants={sectionAnimation}
            className="space-y-4"
          >
            <DateSelect
              workingDays={getWorkingDaysForSelectedDoctor()}
              onSelect={handleDateSelect}
              selectedDate={selectedDate}
            />
          </motion.section>
        )}

        {selectedDate && (
          <motion.section
            ref={timeSectionRef}
            initial="hidden"
            animate="visible"
            custom={4}
            variants={sectionAnimation}
            className="space-y-4"
          >
            <TimeSlotSelect
              selectedDate={selectedDate}
              workingDay={
                generalOptionSelected
                  ? getWorkingDaysForGeneral().find(
                      (day) =>
                        new Date(selectedDate).getDay() === day.dayOfWeek,
                    )
                  : getWorkingDaysForSelectedDoctor().find(
                      (day) =>
                        new Date(selectedDate).getDay() === day.dayOfWeek,
                    )
              }
              onSelect={handleTimeSlotSelect}
              selectedSlot={selectedTimeSlot}
            />
          </motion.section>
        )}

        {selectedTimeSlot && selectedTimeSlot.isHourly && (
          <motion.section
            ref={hourSectionRef}
            initial="hidden"
            animate="visible"
            custom={4}
            variants={sectionAnimation}
            className="space-y-4"
          >
            <Card className="bg-cardColor rounded-xl shadow-lg border border-primary/20">
              <h3 className="text-xl font-bold text-textPrimary mb-4 flex items-center gap-2">
                <FaClock className="text-primary" /> {t("booking.selectHour")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {generateHourlySlots(
                  selectedTimeSlot.startTime,
                  selectedTimeSlot.endTime,
                ).map((hour, index) => (
                  <Button
                    key={index}
                    type=""
                    onClick={() => handleHourSelect(hour)}
                    className={`h-12 text-sm font-medium transition-all duration-200 ${
                      selectedHour?.startTime === hour.startTime
                        ? "bg-primary border-primary text-white shadow-md"
                        : "bg-white border-gray-300 text-textPrimary hover:bg-primary hover:border-primary hover:text-white"
                    }`}
                  >
                    {formatTimeOnly(hour.startTime, i18n.language)} -{" "}
                    {formatTimeOnly(hour.endTime, i18n.language)}
                  </Button>
                ))}
              </div>
            </Card>
          </motion.section>
        )}

        {selectedTimeSlot && (!selectedTimeSlot.isHourly || selectedHour) && (
          <motion.section
            ref={confirmationSectionRef}
            initial="hidden"
            animate="visible"
            custom={6}
            variants={sectionAnimation}
            className="space-y-6"
          >
            <Card className="bg-cardColor mx-auto rounded-xl shadow-lg transition-transform border border-primary/20">
              <h3 className="text-xl font-bold text-textPrimary mb-6 flex items-center gap-2">
                <GrNotes className="text-primary" />{" "}
                {t("booking.appointmentSummary")}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-placeHolder flex items-center gap-2">
                    <FaClinicMedical className="text-primary/70" size={16} />{" "}
                    {t("booking.clinic")}
                  </span>
                  <span className="font-semibold text-textPrimary">
                    {selectedClinic.clinicName}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-placeHolder flex items-center gap-2">
                    <FaUserMd className="text-primary/70" size={16} />{" "}
                    {t("booking.doctor")}
                  </span>
                  <span className="font-semibold text-textPrimary">
                    {selectedDoctor
                      ? `Dr. ${selectedDoctor.doctorName}`
                      : t("booking.generalAppointment")}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-placeHolder flex items-center gap-2">
                    <FaCalendarAlt className="text-primary/70" size={16} />{" "}
                    {t("booking.date")}
                  </span>
                  <span className="font-semibold text-textPrimary">
                    {dayjs(selectedDate)
                      .locale(currentLanguage === "ar" ? "ar" : "en")
                      .format(
                        currentLanguage === "ar"
                          ? "D MMMM YYYY"
                          : "MMMM DD, YYYY",
                      )}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-placeHolder flex items-center gap-2">
                    <FaClock className="text-primary/70" size={16} />{" "}
                    {t("booking.time")}
                  </span>
                  <span className="font-semibold text-textPrimary">
                    {selectedTimeSlot.isHourly && selectedHour
                      ? `${formatTime(selectedHour.startTime)} - ${formatTime(selectedHour.endTime)}`
                      : `${formatTime(selectedTimeSlot.startTime)} - ${formatTime(selectedTimeSlot.endTime)}`}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-placeHolder flex items-center gap-2">
                    <GrNotes className="text-primary/70" size={16} />{" "}
                    {t("booking.notes")}
                  </span>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t("booking.notesPlaceholder")}
                    className="border border-gray-300 rounded px-2 py-1 w-full "
                    disabled={isBooking}
                  />
                </div>
                <Button
                  type=""
                  size="large"
                  loading={isBooking}
                  onClick={handleBookAppointment}
                  disabled={isBooking}
                  className={` ${isBooking ? "bg-gray-500" : "bg-goldy"} w-full text-white `}
                >
                  {isBooking
                    ? t("booking.booking")
                    : t("booking.bookAppointment")}
                </Button>
              </div>
            </Card>
          </motion.section>
        )}
      </div>
    </div>
  );
}
